import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { GRADE_LEVELS, OPERATIONS, OPERATION_LABEL, OPERATION_SYMBOL, getDefaultRange } from '../data/mathFacts';
import '../styles/session.css';
import '../styles/math.css';

const PROBLEM_COUNTS = [5, 10, 15, 20, 25];
const ALL_OPERATIONS = Object.values(OPERATIONS);

const STEPS = [
  { num: 1, label: 'Operations' },
  { num: 2, label: 'Numbers' },
  { num: 3, label: 'Settings' },
];

const HINT_TEXT = {
  1: 'Pick one or more operations — the drill will rotate between them.',
  2: "Set the number range next, or apply a grade-level recommendation if you picked one.",
  3: 'Review your settings and hit Start Drill to begin!',
};

const TogglePills = ({ options, value, onChange }) => (
  <div className="toggle-pills">
    {options.map(opt => (
      <button
        key={opt.value}
        type="button"
        className={`toggle-pill ${value === opt.value ? 'active' : ''}`}
        onClick={() => onChange(opt.value)}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

const MultiTogglePills = ({ options, value, onChange }) => (
  <div className="toggle-pills">
    {options.map(opt => (
      <button
        key={opt.value}
        type="button"
        className={`toggle-pill ${value.includes(opt.value) ? 'active' : ''}`}
        onClick={() => onChange(opt.value)}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

function NumberChipInput({ label, placeholder, numbers, onAdd, onRemove }) {
  const [text, setText] = useState('');

  const commit = () => {
    const n = parseInt(text, 10);
    if (!Number.isNaN(n) && !numbers.includes(n)) onAdd(n);
    setText('');
  };

  return (
    <div className="wz-field">
      <label className="wz-field-label">{label}</label>
      <div className="math-range-row">
        <input
          className="math-chip-input"
          type="number"
          placeholder={placeholder}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
        />
        <button type="button" className="wz-back-btn" onClick={commit}>Add</button>
      </div>
      {numbers.length > 0 && (
        <div className="math-chip-row">
          {numbers.map(n => (
            <span key={n} className="math-chip">
              {n}
              <span className="math-chip-remove" onClick={() => onRemove(n)}>✕</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export const MathLibraryPage = () => {
  const navigate = useNavigate();
  const [wizardStep, setWizardStep] = useState(1);

  const [operations, setOperations] = useState([OPERATIONS.ADDITION]);
  const [gradeId, setGradeId] = useState(''); // optional — '' means no recommendation

  const [rangeMin, setRangeMin] = useState(0);
  const [rangeMax, setRangeMax] = useState(10);

  const [includeNumbers, setIncludeNumbers] = useState([]);
  const [excludeNumbers, setExcludeNumbers] = useState([]);

  const [problemCount, setProblemCount] = useState(10);
  const [timeLimitEnabled, setTimeLimitEnabled] = useState(false);
  const [timeLimitMinutes, setTimeLimitMinutes] = useState(2);
  const [answerMode, setAnswerMode] = useState('type'); // 'type' | 'choice'

  const grade = gradeId ? GRADE_LEVELS.find(g => g.id === gradeId) : null;

  const recommendedRange = useMemo(() => {
    if (!gradeId) return null;
    return operations.reduce((acc, op) => {
      const r = getDefaultRange(gradeId, op);
      return { min: Math.min(acc.min, r.min), max: Math.max(acc.max, r.max) };
    }, { min: Infinity, max: -Infinity });
  }, [gradeId, operations]);

  const toggleOperation = (op) => {
    setOperations(prev => {
      if (prev.includes(op)) {
        return prev.length > 1 ? prev.filter(o => o !== op) : prev;
      }
      return [...prev, op];
    });
  };

  const applyRecommendedRange = () => {
    if (!recommendedRange) return;
    setRangeMin(recommendedRange.min);
    setRangeMax(recommendedRange.max);
  };

  const rangeValid = Number(rangeMin) <= Number(rangeMax);

  const startDrill = () => {
    const config = {
      gradeLabel: grade?.label || null,
      operations,
      range: { min: Number(rangeMin), max: Number(rangeMax) },
      includeNumbers,
      excludeNumbers,
      count: problemCount,
      timeLimitMs: timeLimitEnabled ? timeLimitMinutes * 60 * 1000 : null,
      answerMode,
    };
    navigate('/math/practice', { state: { config } });
  };

  return (
    <div className="wz-page">
      <div className="session-header-bar">
        <button className="sc-back-link" onClick={() => navigate('/dashboard')}>← Back to Dashboard</button>
        <span className="session-header-title">Math Flash Cards</span>
        <div style={{ width: 120 }} />
      </div>

      <div className="wz-layout">
        <div className="wz-stepper">
          {STEPS.map(({ num, label }) => (
            <div key={num} className="wz-step-item">
              <div className="wz-step-track">
                <div className={`wz-step-circle ${wizardStep === num ? 'wz-active' : wizardStep > num ? 'wz-done' : 'wz-pending'}`}>
                  {wizardStep > num ? '✓' : num}
                </div>
                {num < 3 && <div className={`wz-step-line ${wizardStep > num ? 'wz-line-done' : ''}`} />}
              </div>
              <span className={`wz-step-label ${wizardStep === num ? 'wz-label-active' : ''}`}>{label}</span>
            </div>
          ))}
        </div>

        <div className="wz-main">
          <div className="wz-card">

            {wizardStep === 1 && (
              <>
                <h2 className="wz-step-title">Step 1: Operations</h2>

                <div className="wz-field">
                  <div className="wz-field-row">
                    <label className="wz-field-label">Operations</label>
                    <span className="wz-count-badge">{operations.length} selected</span>
                  </div>
                  <p className="wz-field-hint">Choose one or more — the drill will rotate between them.</p>
                  <MultiTogglePills
                    value={operations}
                    onChange={toggleOperation}
                    options={ALL_OPERATIONS.map(op => ({ value: op, label: `${OPERATION_SYMBOL[op]} ${OPERATION_LABEL[op]}` }))}
                  />
                </div>

                <div className="wz-field">
                  <label className="wz-field-label">Grade Level (optional)</label>
                  <p className="wz-field-hint">Just a number-range recommendation — every operation above is always available no matter what you pick here.</p>
                  <TogglePills
                    value={gradeId}
                    onChange={setGradeId}
                    options={[{ value: '', label: 'No recommendation' }, ...GRADE_LEVELS.map(g => ({ value: g.id, label: g.label }))]}
                  />
                </div>
              </>
            )}

            {wizardStep === 2 && (
              <>
                <h2 className="wz-step-title">Step 2: Numbers</h2>

                {grade && recommendedRange && (
                  <div className="wz-summary" style={{ marginBottom: 20 }}>
                    <div className="wz-summary-row">
                      <span className="wz-summary-label">Recommended for {grade.label}</span>
                      <span className="wz-summary-value">
                        {recommendedRange.min}–{recommendedRange.max}{' '}
                        <button type="button" className="wz-back-btn" style={{ marginLeft: 8 }} onClick={applyRecommendedRange}>
                          Use this
                        </button>
                      </span>
                    </div>
                  </div>
                )}

                <div className="wz-field">
                  <label className="wz-field-label">Number Range</label>
                  <p className="wz-field-hint">
                    {operations.includes(OPERATIONS.DIVISION)
                      ? 'Applies to every operation selected. For division, it sets the divisor and quotient — the dividend is calculated so answers are always whole numbers.'
                      : 'Applies to both numbers in every problem.'}
                  </p>
                  <div className="math-range-row">
                    <input className="math-range-input" type="number" value={rangeMin} onChange={e => setRangeMin(e.target.value)} />
                    <span>to</span>
                    <input className="math-range-input" type="number" value={rangeMax} onChange={e => setRangeMax(e.target.value)} />
                  </div>
                  {!rangeValid && <p className="wz-max-msg">Min must be less than or equal to max.</p>}
                </div>

                <NumberChipInput
                  label="Target Specific Numbers (optional)"
                  placeholder="e.g. 3"
                  numbers={includeNumbers}
                  onAdd={n => setIncludeNumbers(prev => [...prev, n])}
                  onRemove={n => setIncludeNumbers(prev => prev.filter(x => x !== n))}
                />
                <p className="wz-field-hint">If set, one number in every problem is drawn from this list instead of the range above.</p>

                <NumberChipInput
                  label="Exclude Numbers (optional)"
                  placeholder="e.g. 0"
                  numbers={excludeNumbers}
                  onAdd={n => setExcludeNumbers(prev => [...prev, n])}
                  onRemove={n => setExcludeNumbers(prev => prev.filter(x => x !== n))}
                />
              </>
            )}

            {wizardStep === 3 && (
              <>
                <h2 className="wz-step-title">Step 3: Session Settings</h2>

                <div className="wz-field">
                  <label className="wz-field-label">Answer Mode</label>
                  <TogglePills
                    value={answerMode}
                    onChange={setAnswerMode}
                    options={[
                      { value: 'type', label: 'Type the Number' },
                      { value: 'choice', label: 'Multiple Choice' },
                    ]}
                  />
                </div>

                <div className="wz-field">
                  <label className="wz-field-label">Number of Problems</label>
                  <select className="trials-select" value={problemCount} onChange={e => setProblemCount(Number(e.target.value))}>
                    {PROBLEM_COUNTS.map(n => <option key={n} value={n}>{n} problems</option>)}
                  </select>
                </div>

                <div className="wz-field">
                  <label className="wz-field-label" style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={timeLimitEnabled}
                      onChange={e => setTimeLimitEnabled(e.target.checked)}
                      style={{ marginRight: 8 }}
                    />
                    Set a time limit
                  </label>
                  {timeLimitEnabled && (
                    <div className="math-range-row" style={{ marginTop: 8 }}>
                      <input
                        className="math-range-input"
                        type="number"
                        min={1}
                        value={timeLimitMinutes}
                        onChange={e => setTimeLimitMinutes(Number(e.target.value))}
                      />
                      <span>minutes</span>
                    </div>
                  )}
                </div>

                <div className="wz-summary">
                  <div className="wz-summary-row">
                    <span className="wz-summary-label">Operations</span>
                    <span className="wz-summary-value">{operations.map(op => OPERATION_LABEL[op]).join(', ')}</span>
                  </div>
                  <div className="wz-summary-row">
                    <span className="wz-summary-label">Range</span>
                    <span className="wz-summary-value">{rangeMin}–{rangeMax}</span>
                  </div>
                  <div className="wz-summary-row">
                    <span className="wz-summary-label">Problems</span>
                    <span className="wz-summary-value">{problemCount}</span>
                  </div>
                  <div className="wz-summary-row">
                    <span className="wz-summary-label">Answer Mode</span>
                    <span className="wz-summary-value">{answerMode === 'type' ? 'Type the Number' : 'Multiple Choice'}</span>
                  </div>
                  {timeLimitEnabled && (
                    <div className="wz-summary-row">
                      <span className="wz-summary-label">Time Limit</span>
                      <span className="wz-summary-value">{timeLimitMinutes} min</span>
                    </div>
                  )}
                </div>
              </>
            )}

          </div>
        </div>
      </div>

      <div className="wz-footer">
        <div className="wz-footer-hint">
          <span className="wz-hint-text">{HINT_TEXT[wizardStep]}</span>
        </div>
        <div className="wz-footer-nav">
          {wizardStep > 1 && (
            <button className="wz-back-btn" type="button" onClick={() => setWizardStep(s => s - 1)}>← Back</button>
          )}
          {wizardStep < 3 ? (
            <button
              className="wz-next-btn"
              type="button"
              disabled={wizardStep === 2 && !rangeValid}
              onClick={() => setWizardStep(s => s + 1)}
            >
              Next →
            </button>
          ) : (
            <button className="wz-next-btn" type="button" onClick={startDrill}>
              Start Drill →
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
